import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";

import { useSourceCitations } from "@/hooks/useSourceCitations";
import { SourceListItem } from "@/components/SourceListItem";
import { useComboCard } from "@/hooks/useComboCard";
import { riskMeta, RISK_META, DOSE_TIER_LABEL, type SubstanceDetail, type RouteWithDoses, type Evidenz, type InteractionContent } from "@/types/restlicht-db";
import { riskLabelKey } from "@/lib/risk-labels";
import { toneAlpha } from "@/lib/tone";
import { AlertTriangle, ChevronDown, ChevronUp, ExternalLink, Loader2, X } from "lucide-react";
import { useT, useLang } from "@/i18n/LanguageContext";
import { pickLang } from "@/lib/pickLang";
import { slugify } from "@/lib/slugify";

interface Props {
  aId: string | null;
  bId: string | null;
  aName?: string;
  bName?: string;
  fallbackLevel?: string;
  fallbackLabel?: string;
  fallbackColor?: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  forceOverlay?: boolean;
}


const PANEL_WIDTH = 420;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: "0.85rem", color: "var(--text-primary)", lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function Empty() {
  const t = useT();
  return <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>{t("combo.emptyData")}</span>;
}

function formatMinutes(min: number | null, max: number | null) {
  if (min == null && max == null) return null;
  const fmt = (n: number) => (n >= 60 ? `${(n / 60).toFixed(n % 60 === 0 ? 0 : 1)} h` : `${n} min`);
  if (min != null && max != null) return min === max ? fmt(min) : `${fmt(min)}-${fmt(max)}`;
  return fmt((min ?? max)!);
}

const DANGER_COLOR = "var(--destructive, var(--danger, #e5484d))";

function formatDoseAmount(d: { amount_min: number | null; amount_max: number | null; unit: string }) {
  const { amount_min: lo, amount_max: hi, unit } = d;
  if (lo != null && hi != null) return lo === hi ? `${lo} ${unit}` : `${lo}-${hi} ${unit}`;
  if (lo != null && hi == null) return `${lo}+ ${unit}`;
  if (lo == null && hi != null) return `${hi} ${unit}`;
  return "";
}

function RouteRow({ r }: { r: RouteWithDoses }) {
  const t = useT();
  const { lang } = useLang();
  const onset = formatMinutes(r.onset_min_minutes, r.onset_max_minutes);
  const dur = formatMinutes(r.duration_min_minutes, r.duration_max_minutes);
  const routeNotes = pickLang(r, "notes", lang);
  return (
    <div style={{ padding: "0.55rem 0.7rem", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, fontSize: "0.8rem", textTransform: "capitalize" }}>{r.route}</span>
        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
          {onset && `${t("combo.onset")} ${onset}`}{onset && dur ? " · " : ""}{dur && `${t("combo.duration")} ${dur}`}
        </span>
      </div>
      {r.substance_doses?.length ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(90px,1fr))", gap: 4, fontSize: "0.75rem" }}>
          {r.substance_doses.map((d) => {
            const isHigh = d.tier === "strong" || d.tier === "heavy";
            const labelColor = isHigh ? DANGER_COLOR : "var(--text-muted)";
            const valueColor = isHigh ? DANGER_COLOR : "var(--text-primary)";
            return (
              <div key={d.id} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ color: labelColor, fontSize: "0.68rem", fontWeight: isHigh ? 600 : 400 }}>{DOSE_TIER_LABEL[d.tier]}</span>
                <span style={{ color: valueColor, fontWeight: isHigh ? 600 : 400 }}>{formatDoseAmount(d)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
      {routeNotes ? (
        <div style={{ marginTop: 4, fontSize: "0.7rem", color: "var(--text-muted)", fontStyle: "italic" }}>{routeNotes}</div>
      ) : null}
    </div>
  );
}


const DOSE_NOTE_STYLE: React.CSSProperties = {
  padding: "0.6rem 0.75rem",
  borderRadius: 8,
  background: "rgba(245, 158, 11, 0.08)",
  border: "1px solid rgba(245, 158, 11, 0.35)",
  fontSize: "0.75rem",
  color: "var(--text-primary)",
  lineHeight: 1.5,
};

/**
 * Shown once per card: every dose on the card is a single-substance figure,
 * so a combination needs both doses lowered, or no combining at all.
 */
function MonoDoseNotice({ riskLevel }: { riskLevel: string | null }) {
  const t = useT();
  const severe = riskLevel === "unsafe" || riskLevel === "dangerous";
  const unknown = riskLevel == null || riskLevel === "unknown";
  return (
    <div
      role="note"
      data-testid="combo-mono-dose-notice"
      style={{
        padding: "0.75rem 0.85rem",
        borderRadius: 10,
        background: "rgba(245, 158, 11, 0.10)",
        border: "1.5px solid rgba(245, 158, 11, 0.55)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        fontSize: "0.8rem",
        lineHeight: 1.5,
        color: "var(--text-primary)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fbbf24", fontWeight: 700, fontSize: "0.8rem" }}>
        <AlertTriangle size={15} />
        {t("combo.monoDose.title")}
      </div>
      <div>{t("combo.monoDose.body")}</div>
      {severe ? <div style={{ fontWeight: 600 }}>{t("combo.monoDose.dangerous")}</div> : null}
      {unknown ? <div>{t("combo.monoDose.unknown")}</div> : null}
    </div>
  );
}

function SubstancePanel({ s }: { s: SubstanceDetail }) {
  const t = useT();
  const { lang } = useLang();
  const desired = s.substance_effects?.filter((e) => e.kind === "desired") ?? [];
  const adverse = s.substance_effects?.filter((e) => e.kind === "adverse") ?? [];
  const summary = pickLang(s.substance_profiles, "summary", lang);
  const doseNote = pickLang(s.substance_profiles, "dose_note", lang);
  const hasDoses = s.substance_routes?.some((r) => r.substance_doses?.length) ?? false;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem", padding: "0.9rem", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>{s.name}</div>
      {summary && (
        <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, var(--text-primary))" }}>{summary}</div>
      )}
      <Section title={t("combo.section.effects")}>
        {desired.length ? <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>{desired.map((e) => <li key={e.id}>{pickLang(e, "text", lang)}</li>)}</ul> : <Empty />}
      </Section>
      <Section title={t("combo.section.adverse")}>
        {adverse.length ? <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>{adverse.map((e) => <li key={e.id}>{pickLang(e, "text", lang)}</li>)}</ul> : <Empty />}
      </Section>
      <Section title={t("combo.section.routes")}>
        {s.substance_routes?.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {doseNote ? <div style={DOSE_NOTE_STYLE}>{doseNote}</div> : null}
            {hasDoses ? (
              <div style={DOSE_NOTE_STYLE}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("combo.monoDose.short")}</div>
                {t("combo.doseDisclaimer")}
              </div>
            ) : null}
            {s.substance_routes.map((r) => <RouteRow key={r.id} r={r} />)}
          </div>
        ) : <Empty />}
      </Section>

    </div>
  );
}

const EVIDENZ_STYLE: Record<Evidenz, { bg: string; border: string; fg: string; key: "ic.evidenz.gesichert" | "ic.evidenz.wahrscheinlich" | "ic.evidenz.unzureichend" }> = {
  gesichert: { bg: "#0d3b2e", border: "#10b98166", fg: "#34d399", key: "ic.evidenz.gesichert" },
  wahrscheinlich: { bg: "#3b2a0d", border: "#f59e0b66", fg: "#fbbf24", key: "ic.evidenz.wahrscheinlich" },
  unzureichend: { bg: "#2a2a2a", border: "#9ca3af55", fg: "#d1d5db", key: "ic.evidenz.unzureichend" },
};

function EvidenzBadge({ evidenz }: { evidenz: Evidenz }) {
  const t = useT();
  const s = EVIDENZ_STYLE[evidenz];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        borderRadius: 999,
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.fg,
        fontSize: "0.72rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {t("ic.evidenz.label")}: {t(s.key)}
    </span>
  );
}

function RichContent({ content, tone, interactionId }: { content: InteractionContent; tone: string; interactionId: number | null }) {
  const t = useT();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const { numbered, domPrefix, highlighted, cite } = useSourceCitations(interactionId, lang, content.quellen, () => setOpen(true));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <EvidenzBadge evidenz={content.evidenz} />
      </div>

      <div
        style={{
          padding: "0.7rem 0.85rem",
          borderRadius: 8,
          background: toneAlpha(tone, 8),
          border: `1px solid ${toneAlpha(tone, 33)}`,
          fontSize: "0.9rem",
          fontWeight: 600,
          color: "var(--text-primary)",
          lineHeight: 1.5,
        }}
      >
        {cite(content.kernaussage)}
      </div>

      <Section title={t("ic.section.wasPassiert")}>{cite(content.was_passiert)}</Section>
      <Section title={t("ic.section.fuerWen")}>{cite(content.fuer_wen)}</Section>

      <div
        style={{
          padding: "0.85rem 0.95rem",
          borderRadius: 10,
          background: "rgba(220, 38, 38, 0.10)",
          border: "1.5px solid rgba(220, 38, 38, 0.55)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fca5a5", fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          <AlertTriangle size={16} />
          {t("ic.section.warnzeichen")}
        </div>
        <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.85rem", color: "var(--text-primary)", lineHeight: 1.5 }}>
          {content.warnzeichen.map((w, i) => <li key={i}>{cite(w)}</li>)}
        </ul>
        <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, var(--text-primary))", lineHeight: 1.5 }}>
          {cite(content.warnzeichen_hinweis)}
        </div>
      </div>

      <Section title={t("ic.section.wennPassiert")}>
        <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
          {content.wenn_passiert.map((w, i) => <li key={i}>{cite(w)}</li>)}
        </ul>
      </Section>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.8rem" }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            background: "transparent",
            border: "none",
            color: "var(--text-primary)",
            cursor: "pointer",
            padding: "0.4rem 0",
            fontSize: "0.85rem",
            fontWeight: 600,
            textAlign: "left",
          }}
        >
          <span>{t("ic.section.experte")}</span>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {open && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem", marginTop: "0.6rem" }}>
            {content.experte.map((p, i) => (
              <p key={i} style={{ margin: 0, fontSize: "0.83rem", color: "var(--text-secondary, var(--text-primary))", lineHeight: 1.6 }}>{cite(p)}</p>
            ))}
            {(content.quellen.length > 0 || numbered.length > 0) && (
              <Section title={t("ic.section.quellen")}>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                  {numbered.map((e) => (
                    <SourceListItem key={e.key} entry={e} domId={`${domPrefix}-${e.n}`} highlighted={highlighted === e.n} cite={cite} />
                  ))}
                </ul>
              </Section>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

export function ComboInfoSheet({ aId, bId, aName, bName, fallbackLabel, fallbackColor, open, onOpenChange, forceOverlay }: Props) {
  const t = useT();
  const { data, loading, error } = useComboCard(aId, bId);
  const interaction = data?.interaction ?? null;
  const meta = interaction ? riskMeta(interaction.risk_level) : null;
  /** Missing assessment stays neutral grey, never the cyan accent. */
  const tone = meta?.tone ?? fallbackColor ?? RISK_META.unknown.tone;
  const label = interaction
    ? t(riskLabelKey(interaction.risk_level))
    : fallbackLabel ?? t("matrix.noAssessment");

  const { lang } = useLang();
  const richContent: InteractionContent | null = interaction
    ? (lang === "en" && interaction.content_en ? interaction.content_en : interaction.content_de)
    : null;

  const [isMobile, setIsMobile] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateRm = () => setReducedMotion(rm.matches);
    updateRm();
    rm.addEventListener?.("change", updateRm);
    return () => {
      mq.removeEventListener?.("change", update);
      rm.removeEventListener?.("change", updateRm);
    };
  }, []);

  const overlay = isMobile || forceOverlay;

  // Body scroll lock (overlay)
  useEffect(() => {
    if (!overlay || !open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [overlay, open]);

  // ESC closes (overlay)
  useEffect(() => {
    if (!overlay || !open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onOpenChange(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [overlay, open, onOpenChange]);

  // Focus management: move focus into the drawer on open, restore on close
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!overlay) return;
    if (open) {
      lastFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
      const id = window.setTimeout(() => {
        (closeBtnRef.current ?? dialogRef.current)?.focus();
      }, 50);
      return () => window.clearTimeout(id);
    } else {
      const prev = lastFocusedRef.current;
      if (prev && document.contains(prev)) {
        try { prev.focus(); } catch {}
      }
    }
  }, [overlay, open]);



  // Swipe-to-close via Pointer Events (Chrome/Safari/iPadOS robust)
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    id: number;
    x: number;
    y: number;
    decided: "?" | "x" | "y";
    panelW: number;
  } | null>(null);

  const onPointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (e.pointerType === "mouse") return;
    const target = e.currentTarget;
    dragRef.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      decided: "?",
      panelW: target.getBoundingClientRect().width || 420,
    };
  };
  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    const s = dragRef.current;
    if (!s || s.id !== e.pointerId) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (s.decided === "?") {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      if (Math.abs(dy) > Math.abs(dx)) { s.decided = "y"; return; }
      s.decided = "x";
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
      setDragging(true);
    }
    if (s.decided !== "x") return;
    setDragX(Math.max(0, dx));
  };
  const finishDrag = (e: React.PointerEvent<HTMLDivElement>, cancelled = false) => {
    const s = dragRef.current;
    if (!s || s.id !== e.pointerId) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    setDragging(false);
    if (!cancelled && dragX > Math.max(80, s.panelW * 0.28)) {
      onOpenChange(false);
      setTimeout(() => setDragX(0), 320);
    } else {
      setDragX(0);
    }
  };
  const onPointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => finishDrag(e, false);
  const onPointerCancel: React.PointerEventHandler<HTMLDivElement> = (e) => finishDrag(e, true);

  const panelInner = (
    <>
      <button
        ref={closeBtnRef}
        onClick={() => onOpenChange(false)}
        aria-label={t("combo.close")}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "transparent",
          border: "none",
          color: "var(--text-muted)",
          cursor: "pointer",
          padding: 6,
          borderRadius: 6,
          zIndex: 1,
        }}
      >
        <X size={18} />
      </button>

      <div id="combo-sheet-title" style={{ fontSize: "1.1rem", fontWeight: 700, paddingRight: 32 }}>
        {data?.a.name ?? aName ?? "…"} <span style={{ color: "var(--text-muted)" }}>+</span> {data?.b.name ?? bName ?? "…"}
      </div>


      {data?.a.name && data?.b.name && (
        <Link
          to="/matrix/$pair"
          params={{ pair: `${slugify(data.a.name)}__${slugify(data.b.name)}` }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            color: "var(--accent-cyan)", textDecoration: "none",
            fontSize: "0.78rem", alignSelf: "flex-start",
          }}
        >
          <ExternalLink size={12} /> Detailseite öffnen
        </Link>
      )}

      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "1.5rem 0", color: "var(--text-muted)" }}>
          <Loader2 size={16} className="animate-spin" /> {t("combo.loading")}
        </div>
      )}
      {error && (
        <div style={{ padding: "0.75rem", color: "#ef4444", fontSize: "0.85rem" }}>{t("combo.error")}: {error}</div>
      )}

      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
          <MonoDoseNotice riskLevel={interaction?.risk_level ?? null} />
          <SubstancePanel s={data.a} />

          <div style={{
            padding: "0.95rem",
            borderRadius: 10,
            background: toneAlpha(tone, 8),
            border: `1px solid ${toneAlpha(tone, 33)}`,
            display: "flex",
            flexDirection: "column",
            gap: "0.7rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <AlertTriangle size={18} style={{ color: tone }} />
              <span
                data-testid="combo-risk-label"
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: tone,
                }}
              >{label}</span>
            </div>

            {richContent ? (
              <RichContent content={richContent} tone={tone} interactionId={interaction?.id ?? null} />
            ) : interaction ? (
              <>
                {interaction.summary && <Section title={t("combo.section.summary")}>{interaction.summary}</Section>}
                {interaction.mechanism && <Section title={t("combo.section.mechanism")}>{interaction.mechanism}</Section>}
                {interaction.effects_combined && <Section title={t("combo.section.combined")}>{interaction.effects_combined}</Section>}
                {interaction.risks && <Section title={t("combo.section.risks")}>{interaction.risks}</Section>}
                {interaction.harm_reduction && <Section title={t("combo.section.harmReduction")}>{interaction.harm_reduction}</Section>}
                {!interaction.summary && !interaction.mechanism && !interaction.risks && (
                  <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                    {t("combo.noDescription")}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                {t("matrix.noAssessmentHint")}
              </div>
            )}
          </div>

          <SubstancePanel s={data.b} />
        </div>
      )}
    </>
  );

  if (isMobile || forceOverlay) {
    const panelW = Math.min(typeof window !== "undefined" ? window.innerWidth * 0.92 : 420, 420);
    const progress = open ? Math.min(1, Math.max(0, 1 - dragX / panelW)) : 0;
    const ease = "cubic-bezier(0.22, 1, 0.36, 1)";
    const dur = reducedMotion ? "0ms" : "300ms";
    const shadowAlpha = 0.45 * progress;
    return (
      <>
        <div
          onClick={() => onOpenChange(false)}
          aria-hidden={!open}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            opacity: progress,
            pointerEvents: open ? "auto" : "none",
            transition: dragging ? "none" : `opacity ${dur} ${ease}`,
            zIndex: 60,
          }}
        />
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="combo-sheet-title"
          aria-hidden={!open}
          tabIndex={-1}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100dvh",
            width: "min(92vw, 420px)",
            transform: open ? `translate3d(${dragX}px,0,0)` : "translate3d(100%,0,0)",
            transition: dragging ? "none" : `transform ${dur} ${ease}, box-shadow ${dur} ${ease}`,
            background: "var(--bg-surface, #0d0d12)",
            color: "var(--text-primary)",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            boxShadow: open ? `-16px 0 40px rgba(0,0,0,${shadowAlpha.toFixed(3)})` : "none",
            padding: "1.1rem 1.25rem calc(1.1rem + env(safe-area-inset-bottom)) 1.25rem",
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            WebkitTapHighlightColor: "transparent",
            userSelect: dragging ? "none" : "auto",
            WebkitUserSelect: dragging ? "none" : "auto",
            display: "flex",
            flexDirection: "column",
            gap: "0.9rem",
            zIndex: 61,
            touchAction: dragging ? "none" : "pan-y",
            willChange: open ? "transform" : "auto",
          }}
        >
          {panelInner}
        </div>
      </>
    );
  }


  return (
    <div
      aria-hidden={!open}
      style={{
        width: open ? PANEL_WIDTH : 0,
        marginLeft: open ? "1rem" : 0,
        overflow: "hidden",
        flexShrink: 0,
        alignSelf: "stretch",
        willChange: "width, margin-left",
        transition:
          "width 500ms cubic-bezier(0.22, 1, 0.36, 1), margin-left 500ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div
        style={{
          width: PANEL_WIDTH,
          transform: open ? "translateX(0)" : "translateX(-100%)",
          opacity: open ? 1 : 0,
          willChange: "transform, opacity",
          transition:
            "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), opacity 500ms cubic-bezier(0.22, 1, 0.36, 1)",
          background: "var(--bg-surface, #0d0d12)",
          color: "var(--text-primary)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12,
          padding: "1.1rem 1.25rem",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: "0.9rem",
        }}
      >
        {panelInner}
      </div>
    </div>
  );
}
